import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import userImg from "../assets/userImg.png";
import api from "../api";
import CustomTextInput from "../components/CustomTextInput";
import Layout from "../components/Layout";
import ConfirmDeletePopup from "../components/ConfirmDeletePopup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../components/Spinner";
import CustomPhoneInput from "../components/CustomPhoneInput";

export default function UserDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [govId, setGovId] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleChangePassword = async () => {
    setPasswordError("");
    if (newPassword !== confirmPassword) {
      setPasswordError("Las contraseñas no coinciden.");
      return;
    }

    try {
      await api.users.updatePassword(id, { new: newPassword });
      toast.success("Contraseña actualizada con éxito");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Error cambiando contraseña:", error);
      setPasswordError(error.detail);
    }
  };

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const roles = [
    { value: 1, label: "Coordinador" },
    { value: 2, label: "Administrador" },
  ];

  useEffect(() => {
    async function fetchUser() {
      try {
        const response = await api.users.getOne(id);
        setUser(response);
        setFirstName(response.firstName);
        setLastName(response.lastName);
        setGovId(response.govId);
        setEmail(response.email);
        setContact(response.phone);
        setRole(response.role);
        setError("");
      } catch (error) {
        console.error("Error fetching user details:", error);
        setError("Error al obtener los detalles del usuario.");
      }
    }
    fetchUser();
  }, [id]);

  const handleSave = async () => {
    setFieldErrors({});
    try {
      const updatedUser = {
        firstName,
        lastName,
        govId,
        email,
        phone: contact,
        role,
      };

      await api.users.update(id, updatedUser);
      setUser(updatedUser);
      toast.success("Datos guardados con éxito");
      setError("");
    } catch (error) {
      if (error.message) {
        setFieldErrors(error.message);
      } else {
        setError("Error al actualizar los datos del usuario.");
      }
    }
  };

  const handleDeleteClick = () => {
    setIsPopupOpen(true); // Abre el popup
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.users.delete(id);
      navigate(`/users`);
      setError("");
    } catch (error) {
      console.error("Error deleting user:", error);
      setError("Error al eliminar el usuario.");
    }
    setIsPopupOpen(false); // Cierra el popup después de la confirmación
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false); // Cierra el popup sin eliminar
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (!user) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [user]);

  if (!user) {
    return <Spinner />;
  }

  return (
    <Layout>
      <div
        className="poppins-medium flex flex-col h-screen bg-[#F8F9FB] w-full md:p-8 transition-opacity ease-in-out"
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <h1 className="poppins-bold text-lg text-left text-[#1F384C] mb-6">
          Detalles del usuario
        </h1>
        <div className="p-8 border rounded-2xl">
          <ToastContainer />
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <div className="flex flex-col items-center mb-10">
            <div className="w-40 h-40 rounded-full overflow-hidden mb-4">
              <img
                src={userImg}
                alt={user.firstName}
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="flex justify-between text-xl">
              <p className="p-2 text-xl">
                {`${user.firstName} ${user.lastName}`}{" "}
                <span className="hidden md:inline p-4">|</span>
              </p>
              <div className="px-6 bg-[#FFC555] border border-[#FFC555] text-sm rounded-3xl flex justify-center items-center">
                {roles.find((r) => r.value === user.role)?.label ||
                  "Rol desconocido"}
              </div>
            </h2>
          </div>

          <div className="flex bg-white rounded-lg shadow-md mb-6 divide-x divide-gray-300">
            <div className="w-full md:w-1/2 p-6 pb-12">
              <h3 className="text-xl mb-6 text-[#595A5C]">
                Información del usuario
              </h3>
              <div className="flex mx-5 justify-between gap-10 flex-wrap">
                {/* Firstname */}
                <div className="flex flex-col items-start gap-2">
                  <>
                    <CustomTextInput
                      label="Nombre"
                      placeholder="Ingresa el nombre del usuario"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    {fieldErrors.firstName && (
                      <p className="text-red-600 text-sm">
                        {fieldErrors.firstName[0]}
                      </p>
                    )}
                  </>
                </div>

                {/* Lastname */}
                <div className="flex flex-col items-start gap-2">
                  <>
                    <CustomTextInput
                      label="Apellido"
                      placeholder="Ingresa el apellido del usuario"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    {fieldErrors.lastName && (
                      <p className="text-red-600 text-sm">
                        {fieldErrors.lastName[0]}
                      </p>
                    )}
                  </>
                </div>

                {/* Role */}
                <div className="flex flex-col items-start gap-2">
                  <>
                    <CustomTextInput
                      label="Rol"
                      placeholder="Seleccionar rol"
                      type="select"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      options={roles}
                    />
                    {fieldErrors.role && (
                      <p className="text-red-600 text-sm">
                        {fieldErrors.role[0]}
                      </p>
                    )}
                  </>
                </div>

                {/* Gov ID */}
                <div className="flex flex-col items-start gap-2">
                  <>
                    <CustomTextInput
                      label="Cédula de identidad"
                      placeholder="Ingresa tu número de documento"
                      type="text"
                      value={govId}
                      onChange={(e) => setGovId(e.target.value)}
                    />
                    {fieldErrors.govId && (
                      <p className="text-red-600 text-sm">
                        {fieldErrors.govId[0]}
                      </p>
                    )}
                  </>
                </div>

                {/* Email */}
                <div className="flex flex-col items-start gap-2">
                  <>
                    <CustomTextInput
                      label="Correo electrónico"
                      placeholder="Ingresa el correo electrónico"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {fieldErrors.email && (
                      <p className="text-red-600 text-sm">
                        {fieldErrors.email[0]}
                      </p>
                    )}
                  </>
                </div>

                {/* Contact */}
                <div className="flex flex-col items-start gap-2">
                  <>
                    <CustomPhoneInput
                      label="Teléfono"
                      placeholder="Escribe el número de contacto"
                      value={contact}
                      onChange={(phone) => setContact(phone)}
                    />
                    {fieldErrors.phone && (
                      <p className="text-red-600 text-sm">
                        {fieldErrors.phone[0]}
                      </p>
                    )}
                  </>
                </div>
              </div>
            </div>
            {/* Nueva sección para cambiar la contraseña */}
            <div className="w-full md:w-1/2 p-6 pb-12">
              <h3 className="text-xl mb-6 text-[#595A5C]">
                Cambiar contraseña
              </h3>
              <div className="flex flex-col gap-6">
                <CustomTextInput
                  label="Nueva contraseña"
                  placeholder="Escribe la nueva contraseña"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <CustomTextInput
                  label="Confirmar contraseña"
                  placeholder="Confirma la nueva contraseña"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {passwordError && (
                  <p className="text-red-600 text-sm">{passwordError}</p>
                )}
                <button
                  onClick={handleChangePassword}
                  className="bg-[#59A8D4] text-white text-xs py-4 px-4 rounded-2xl tracking-wider self-end"
                >
                  Cambiar contraseña
                </button>
              </div>
            </div>
          </div>

          <div className="md:space-x-4 space-x-3 flex justify-between">
            <button
              onClick={handleSave}
              className="bg-[#59A8D4] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Guardar
            </button>
            <button
              onClick={handleDeleteClick}
              className="bg-[#BE202F] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Eliminar
            </button>
          </div>
        </div>
        <ConfirmDeletePopup
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          onConfirm={handleDeleteConfirm}
          itemName={user.firstName + " " + user.lastName}
        />
      </div>
    </Layout>
  );
}
